/**
 * Methodology Builder Page
 * 
 * AI-powered methodology generation from Terms of Reference (ToR)
 * Access restricted to admin users only (si@iodparc.com)
 */

import React, { useState, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    Chip,
    Divider,
    Grid,
    Tooltip,
    Stack,
    CircularProgress,
    TextField,
    Snackbar,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Refresh as RefreshIcon,
    Description as DescriptionIcon,
    Search as SearchIcon,
    AutoFixHigh as AutoFixHighIcon,
    Info as InfoIcon,
    Close as CloseIcon,
    ContentCopy as ContentCopyIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import ExtractedRequirementsSummary from '../components/ExtractedRequirementsSummary';
import CVMatchingResults from '../components/CVMatchingResults';
import {
    TorExtractionResult,
    SimilarProposal,
    MethodologyGenerationResult,
    isMethodologyBuilderConfigured
} from '../services/methodology';
import { matchCVsFromTor, CVMatchingResult } from '../services/cvMatching';

interface UploadedFile {
    file: File;
    name: string;
    size: string;
}

interface EditableRequirements {
    required_methods: string[];
    recommended_methods: string[];
    required_frameworks: string[];
    specific_techniques: string[];
    additional_context: string;
}

const MethodologyBuilderPage: React.FC = () => {
    // State management
    const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Review/Edit stage
    const [showReview, setShowReview] = useState(false);
    const [editableRequirements, setEditableRequirements] = useState<EditableRequirements | null>(null);
    const [newMethod, setNewMethod] = useState('');
    const [newFramework, setNewFramework] = useState('');
    const [newTechnique, setNewTechnique] = useState('');
    
    // Results
    const [extraction, setExtraction] = useState<TorExtractionResult | null>(null);
    const [similarProposals, setSimilarProposals] = useState<SimilarProposal[]>([]);
    const [methodology, setMethodology] = useState<MethodologyGenerationResult | null>(null);
    const [fullTorText, setFullTorText] = useState<string>('');
    
    // CV Matching
    const [cvMatches, setCvMatches] = useState<CVMatchingResult | null>(null);
    const [isMatchingCVs, setIsMatchingCVs] = useState(false);
    
    // Copy notification
    const [showCopyNotification, setShowCopyNotification] = useState(false);
    
    /**
     * Get final extraction with user edits merged in
     */
    const getFinalExtraction = useCallback(() => {
        if (!extraction || !editableRequirements) return extraction;
        
        // Merge edited requirements back into extraction for display
        return {
            ...extraction,
            methodology_requirements: {
                ...extraction.methodology_requirements,
                required_methods: editableRequirements.required_methods,
                recommended_methods: editableRequirements.recommended_methods,
                required_frameworks: editableRequirements.required_frameworks,
                specific_techniques: editableRequirements.specific_techniques,
            }
        };
    }, [extraction, editableRequirements]);
    
    // Tab state for results view
    const [activeTab, setActiveTab] = useState(0);
    
    // Check if service is configured
    const isConfigured = isMethodologyBuilderConfigured();
    
    /**
     * Process a file (used by both file input and drag-drop)
     */
    const processFile = useCallback((file: File) => {
        // Validate file type
        if (!file.type.includes('pdf') && !file.type.includes('text')) {
            setError('Please upload a PDF or TXT file');
            return;
        }
        
        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
            setError('File size must be less than 10MB');
            return;
        }
        
        setUploadedFile({
            file,
            name: file.name,
            size: formatFileSize(file.size)
        });
        setError(null);
    }, []);
    
    /**
     * Handle file selection from input
     */
    const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            processFile(file);
        }
    }, [processFile]);
    
    /**
     * Handle drag and drop
     */
    const handleDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
    }, []);
    
    const handleDrop = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        const file = event.dataTransfer.files?.[0];
        if (file) {
            processFile(file);
        }
    }, [processFile]);
    
    /**
     * Extract ToR requirements (Stage 1)
     */
    const handleGenerate = useCallback(async () => {
        if (!uploadedFile) return;
        
        setIsProcessing(true);
        setError(null);
        setExtraction(null);
        setSimilarProposals([]);
        setMethodology(null);
        setShowReview(false);
        
        try {
            // Import uploadTor from the service
            const { uploadTor } = await import('../services/methodology');
            
            // Extract from ToR
            const extractionResult = await uploadTor(uploadedFile.file);
            
            setExtraction(extractionResult);
            setFullTorText(extractionResult.full_tor_text || '');
            
            // Initialize editable requirements from extraction
            setEditableRequirements({
                required_methods: extractionResult.methodology_requirements.required_methods || [],
                recommended_methods: extractionResult.methodology_requirements.recommended_methods || [],
                required_frameworks: extractionResult.methodology_requirements.required_frameworks || [],
                specific_techniques: extractionResult.methodology_requirements.specific_techniques || [],
                additional_context: ''
            });
            
            // Show review page
            setShowReview(true);
            
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setIsProcessing(false);
        }
    }, [uploadedFile]);
    
    /**
     * Continue with search and generation (Stage 2 & 3)
     */
    const handleContinueGeneration = useCallback(async () => {
        if (!extraction || !editableRequirements || !uploadedFile) {
            setError('Missing required data. Please start over.');
            return;
        }
        
        setIsProcessing(true);
        setError(null);
        setShowReview(false);
        
        try {
            const { uploadTor, findSimilarProposals, generateMethodology } = await import('../services/methodology');
            
            let finalExtraction = extraction;
            
            // Check if user made edits
            const hasEdits = 
                editableRequirements.additional_context.trim() !== '' ||
                JSON.stringify(editableRequirements.required_methods) !== JSON.stringify(extraction.methodology_requirements.required_methods) ||
                JSON.stringify(editableRequirements.recommended_methods) !== JSON.stringify(extraction.methodology_requirements.recommended_methods) ||
                JSON.stringify(editableRequirements.required_frameworks) !== JSON.stringify(extraction.methodology_requirements.required_frameworks) ||
                JSON.stringify(editableRequirements.specific_techniques) !== JSON.stringify(extraction.methodology_requirements.specific_techniques);
            
            // If user edited, regenerate query_vector
            if (hasEdits) {
                // Create edited text combining original + edits
                const editedText = `
Original ToR Requirements:
${JSON.stringify(extraction, null, 2)}

EDITED REQUIREMENTS:
Required Methods: ${editableRequirements.required_methods.join(', ')}
Recommended Methods: ${editableRequirements.recommended_methods.join(', ')}
Required Frameworks: ${editableRequirements.required_frameworks.join(', ')}
Specific Techniques: ${editableRequirements.specific_techniques.join(', ')}

ADDITIONAL CONTEXT:
${editableRequirements.additional_context}
                `.trim();
                
                // Regenerate extraction with edited requirements
                const editedFile = new File([editedText], 'edited-requirements.txt', { type: 'text/plain' });
                finalExtraction = await uploadTor(editedFile);
            }
            
            // Stage 2: Find similar proposals
            if (!finalExtraction.query_vector) {
                throw new Error('ToR extraction did not return query_vector. Cannot search for similar proposals.');
            }
            
            const proposals = await findSimilarProposals(finalExtraction.query_vector);
            setSimilarProposals(proposals);
            
            // Stage 3: Generate methodology
            
            const result = await generateMethodology(
                finalExtraction.project_title || 'Untitled ToR',
                finalExtraction,
                proposals,
                uploadedFile.name
            );
            
            setMethodology(result);
            
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setIsProcessing(false);
        }
    }, [extraction, editableRequirements, uploadedFile]);
    
    /**
     * Find matching CVs from ToR requirements
     */
    const handleFindMatchingCVs = useCallback(async () => {
        if (!extraction) return;
        
        setIsMatchingCVs(true);
        setError(null);
        
        try {
            const result = await matchCVsFromTor(
                { requirements: extraction },
                extraction.query_vector,
                20
            );
            setCvMatches(result);
        } catch (err) {
            console.error('CV matching failed:', err);
            setError(err instanceof Error ? err.message : 'Failed to match CVs');
        } finally {
            setIsMatchingCVs(false);
        }
    }, [extraction]);
    
    /**
     * Reset all state
     */
    const handleReset = useCallback(() => {
        setUploadedFile(null);
        setExtraction(null);
        setSimilarProposals([]);
        setMethodology(null);
        setShowReview(false);
        setEditableRequirements(null);
        setError(null);
        setCvMatches(null);
    }, []);
    
    /**
     * Add item to requirements array
     */
    const handleAddItem = useCallback((field: keyof EditableRequirements, value: string) => {
        if (!editableRequirements || !value.trim()) return;
        
        setEditableRequirements(prev => {
            if (!prev) return prev;
            const currentArray = prev[field];
            if (Array.isArray(currentArray)) {
                return {
                    ...prev,
                    [field]: [...currentArray, value.trim()]
                };
            }
            return prev;
        });
        
        // Clear input
        if (field === 'required_methods' || field === 'recommended_methods') setNewMethod('');
        if (field === 'required_frameworks') setNewFramework('');
        if (field === 'specific_techniques') setNewTechnique('');
    }, [editableRequirements]);
    
    /**
     * Remove item from requirements array
     */
    const handleRemoveItem = useCallback((field: keyof EditableRequirements, index: number) => {
        if (!editableRequirements) return;
        
        setEditableRequirements(prev => {
            if (!prev) return prev;
            const currentArray = prev[field];
            if (Array.isArray(currentArray)) {
                return {
                    ...prev,
                    [field]: currentArray.filter((_, i) => i !== index)
                };
            }
            return prev;
        });
    }, [editableRequirements]);
    
    /**
     * Format file size
     */
    function formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }
    
    /**
     * Clean text by removing all markdown formatting
     */
    const cleanMarkdown = (text: string): string => {
        return text
            .replace(/\*\*/g, '')           // Remove bold **
            .replace(/\*/g, '')             // Remove italic *
            .replace(/^#{1,6}\s+/gm, '')    // Remove markdown headings ###
            .replace(/^>\s+/gm, '')         // Remove blockquotes
            .replace(/`{1,3}/g, '')         // Remove code blocks
            .replace(/^\s*[-*+]\s+/gm, '')  // Remove bullet points (keep text)
            .replace(/^\s*\d+\.\s+/gm, '')  // Remove numbered lists (keep text)
            .trim();
    };
    
    /**
     * Format methodology text for display with proper headings
     */
    const formatMethodologyText = (text: string) => {
        // Use the same cleaning function for consistency
        const cleanText = cleanMarkdown(text);
        
        // Split into lines
        const lines = cleanText.split('\n');
        const elements: React.ReactElement[] = [];
        
        lines.forEach((line, index) => {
            const trimmed = line.trim();
            if (!trimmed) return;
            
            // Detect markdown-style headings (###, ####)
            const h3Match = line.match(/^###\s+(.+)/);
            const h4Match = line.match(/^####\s+(.+)/);
            
            // Check if line is a heading
            const isMainHeading = h3Match || (trimmed === trimmed.toUpperCase() && trimmed.length > 3 && trimmed.length < 100);
            const isSubheading = h4Match || (trimmed.endsWith(':') && !trimmed.includes('.') && trimmed.length < 100);
            const isNumberedItem = /^\d+\./.test(trimmed);
            
            if (isMainHeading) {
                const headingText = h3Match ? h3Match[1] : trimmed;
                elements.push(
                    <Typography
                        key={index}
                        variant="h5"
                        sx={{
                            mt: 4,
                            mb: 2,
                            fontWeight: 700,
                            color: 'primary.main',
                            borderBottom: '2px solid',
                            borderColor: 'primary.light',
                            pb: 1
                        }}
                    >
                        {headingText}
                    </Typography>
                );
            } else if (isSubheading) {
                const subheadingText = h4Match ? h4Match[1] : trimmed;
                elements.push(
                    <Typography
                        key={index}
                        variant="h6"
                        sx={{
                            mt: 3,
                            mb: 1.5,
                            fontWeight: 600,
                            color: 'text.primary'
                        }}
                    >
                        {subheadingText}
                    </Typography>
                );
            } else if (isNumberedItem) {
                elements.push(
                    <Typography
                        key={index}
                        variant="body1"
                        sx={{
                            lineHeight: 1.8,
                            color: 'text.primary',
                            mb: 1,
                            ml: 2,
                            fontWeight: 500
                        }}
                    >
                        {trimmed}
                    </Typography>
                );
            } else {
                elements.push(
                    <Typography
                        key={index}
                        variant="body1"
                        paragraph
                        sx={{
                            lineHeight: 1.8,
                            textAlign: 'justify',
                            color: 'text.secondary',
                            mb: 2
                        }}
                    >
                        {trimmed}
                    </Typography>
                );
            }
        });
        
        return elements;
    };
    
    /**
     * Copy methodology text to clipboard
     */
    const handleCopyMethodology = () => {
        const text = methodology?.generated_methodology || methodology?.methodology_text || '';
        const cleanText = cleanMarkdown(text);
        
        navigator.clipboard.writeText(cleanText);
        setShowCopyNotification(true);
    };
    
    // Show configuration warning if not set up
    if (!isConfigured) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Methodology Builder Not Configured
                    </Typography>
                    <Typography>
                        The Methodology Builder service is not configured. Please contact your administrator
                        to set up the required environment variables.
                    </Typography>
                </Alert>
            </Box>
        );
    }
    
    return (
        <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoFixHighIcon fontSize="large" />
                    Methodology Builder
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    AI-powered methodology generation from your Terms of Reference. 
                    Upload a ToR document and get a professional, tailored methodology suggestions in 60 seconds.
                </Typography>
            </Box>
            
            {/* Info Banner */}
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
                <strong>How it works:</strong> Upload your ToR → AI extracts requirements → 
                Finds 5 similar past proposals → Generates comprehensive, tailored methodology
            </Alert>
            
            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            
            {/* Main Content */}
            {!methodology && !showReview ? (
                /* Upload View - Show upload area only when no methodology generated */
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Paper elevation={2} sx={{ p: 3 }}>
                            {/* File Upload Area */}
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CloudUploadIcon />
                                Upload Terms of Reference
                            </Typography>
                                
                                <Box
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                    sx={{
                                        border: '2px dashed',
                                        borderColor: uploadedFile ? 'primary.main' : 'divider',
                                        borderRadius: 2,
                                        p: 4,
                                        textAlign: 'center',
                                        bgcolor: uploadedFile ? 'action.hover' : 'background.default',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: 'action.hover'
                                        }
                                    }}
                                >
                                    {uploadedFile ? (
                                        <Box>
                                            <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                                            <Typography variant="body1" fontWeight={600}>
                                                {uploadedFile.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {uploadedFile.size}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box>
                                            <CloudUploadIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
                                            <Typography variant="body1" gutterBottom>
                                                Drag and drop your ToR here
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                or
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    <input
                                        type="file"
                                        accept=".pdf,.txt,application/pdf,text/plain"
                                        onChange={handleFileSelect}
                                        style={{ display: 'none' }}
                                        id="tor-file-input"
                                        disabled={isProcessing}
                                    />
                                    <label htmlFor="tor-file-input">
                                        <Button
                                            variant="contained"
                                            component="span"
                                            disabled={isProcessing}
                                            sx={{ mt: 2 }}
                                        >
                                            {uploadedFile ? 'Change File' : 'Select File'}
                                        </Button>
                                    </label>
                                    
                                    <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
                                        Supported formats: PDF, TXT (Max 10MB)
                                    </Typography>
                                </Box>
                                
                                {/* Generate Button */}
                                <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={handleGenerate}
                                        disabled={!uploadedFile || isProcessing}
                                        startIcon={isProcessing ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                                    >
                                        {isProcessing ? 'Generating...' : 'Generate Methodology Suggestions'}
                                    </Button>
                                </Box>
                        </Paper>
                    </Grid>
                </Grid>
            ) : showReview && editableRequirements && extraction ? (
                /* Review/Edit View - Show extracted requirements for editing */
                <Grid container spacing={3}>
                    {/* Header Section */}
                    <Grid item xs={12}>
                        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 1 }}>
                            <Typography variant="body1" fontWeight={600}>
                                Review the extracted requirements below. Add or remove items to refine your search for similar proposals.
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                The extraction found {
                                    editableRequirements.required_methods.length + 
                                    editableRequirements.required_frameworks.length + 
                                    editableRequirements.specific_techniques.length
                                } methodology items. You can customize these before continuing.
                            </Typography>
                        </Alert>
                    </Grid>
                    
                    {/* Comprehensive Extraction Summary */}
                    <Grid item xs={12}>
                        <Paper elevation={3} sx={{ p: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.main' }}>
                            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
                                Extracted Requirements Summary
                            </Typography>
                            
                            <Grid container spacing={3}>
                                {/* Project Overview */}
                                {(extraction.project_title || extraction.client_organization) && (
                                    <Grid item xs={12}>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="overline" color="text.secondary">PROJECT OVERVIEW</Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            <Grid container spacing={2}>
                                                {extraction.project_title && (
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="body2" color="text.secondary">Title</Typography>
                                                        <Typography variant="body1" fontWeight={600}>{extraction.project_title}</Typography>
                                                    </Grid>
                                                )}
                                                {extraction.client_organization && (
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="body2" color="text.secondary">Client</Typography>
                                                        <Typography variant="body1" fontWeight={600}>{extraction.client_organization}</Typography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Box>
                                    </Grid>
                                )}
                                
                                {/* Methodology Requirements */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AutoFixHighIcon color="primary" />
                                        Methodology Requirements
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        {extraction.methodology_requirements.required_methods.length > 0 && (
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Required Methods:</Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {extraction.methodology_requirements.required_methods.map((m, i) => (
                                                        <Chip key={i} label={m} size="small" color="primary" />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                        {extraction.methodology_requirements.recommended_methods.length > 0 && (
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Recommended Methods:</Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {extraction.methodology_requirements.recommended_methods.map((m, i) => (
                                                        <Chip key={i} label={m} size="small" variant="outlined" />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                        {extraction.methodology_requirements.required_frameworks.length > 0 && (
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Frameworks:</Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {extraction.methodology_requirements.required_frameworks.map((f, i) => (
                                                        <Chip key={i} label={f} size="small" color="secondary" />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                        {extraction.methodology_requirements.specific_techniques.length > 0 && (
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Techniques:</Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {extraction.methodology_requirements.specific_techniques.map((t, i) => (
                                                        <Chip key={i} label={t} size="small" color="success" />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                        {extraction.methodology_requirements.required_methods.length === 0 && 
                                         extraction.methodology_requirements.recommended_methods.length === 0 && 
                                         extraction.methodology_requirements.required_frameworks.length === 0 && 
                                         extraction.methodology_requirements.specific_techniques.length === 0 && (
                                            <Alert severity="warning" sx={{ mt: 1 }}>
                                                <Typography variant="body2">
                                                    No specific methodology requirements found. Consider adding methods and frameworks below.
                                                </Typography>
                                            </Alert>
                                        )}
                                    </Stack>
                                </Grid>
                                
                                {/* Evaluation Questions */}
                                {extraction.evaluation_questions.length > 0 && (
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <InfoIcon color="info" />
                                            Evaluation Questions ({extraction.evaluation_questions.length})
                                        </Typography>
                                        <Stack spacing={0.5}>
                                            {extraction.evaluation_questions.slice(0, 5).map((q, i) => (
                                                <Typography key={i} variant="body2" sx={{ display: 'flex', gap: 1 }}>
                                                    <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>•</Box>
                                                    {q}
                                                </Typography>
                                            ))}
                                            {extraction.evaluation_questions.length > 5 && (
                                                <Typography variant="caption" color="text.secondary">
                                                    +{extraction.evaluation_questions.length - 5} more questions
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Grid>
                                )}
                                
                                {/* Scope */}
                                {(extraction.scope.geographic || extraction.scope.timeframe || extraction.scope.target_populations.length > 0) && (
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                            Scope & Coverage
                                        </Typography>
                                        <Stack spacing={1}>
                                            {extraction.scope.geographic && (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">Geographic:</Typography>
                                                    <Typography variant="body2">{extraction.scope.geographic}</Typography>
                                                </Box>
                                            )}
                                            {extraction.scope.timeframe && (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">Timeframe:</Typography>
                                                    <Typography variant="body2">{extraction.scope.timeframe}</Typography>
                                                </Box>
                                            )}
                                            {extraction.scope.target_populations.length > 0 && (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">Target Populations:</Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                        {extraction.scope.target_populations.map((p, i) => (
                                                            <Chip key={i} label={p} size="small" />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Grid>
                                )}
                                
                                {/* Deliverables */}
                                {extraction.deliverables.length > 0 && (
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                            Deliverables ({extraction.deliverables.length})
                                        </Typography>
                                        <Stack spacing={0.5}>
                                            {extraction.deliverables.map((d, i) => (
                                                <Typography key={i} variant="body2" sx={{ display: 'flex', gap: 1 }}>
                                                    <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>✓</Box>
                                                    {d}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>
                    </Grid>
                    
                    {/* Edit Section Header */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            Refine Requirements (Optional)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Add or remove items below to customize your search for similar proposals
                        </Typography>
                    </Grid>
                    
                    {/* Required Methods */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                Required Methods
                                <Chip 
                                    label={`${editableRequirements.required_methods.length} found`} 
                                    size="small" 
                                    color={editableRequirements.required_methods.length > 0 ? "success" : "default"}
                                />
                            </Typography>
                            
                            {editableRequirements.required_methods.length > 0 ? (
                                <Box sx={{ mb: 2 }}>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {editableRequirements.required_methods.map((method, index) => (
                                            <Chip
                                                key={index}
                                                label={method}
                                                onDelete={() => handleRemoveItem('required_methods', index)}
                                                color="primary"
                                                sx={{ mb: 1 }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            ) : (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        No methods detected. Add some below to improve matching.
                                    </Typography>
                                </Alert>
                            )}
                            
                            <Divider sx={{ mb: 2 }} />
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Add more methods:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="e.g., Mixed methods, Quantitative survey"
                                    value={newMethod}
                                    onChange={(e) => setNewMethod(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleAddItem('required_methods', newMethod);
                                        }
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={() => handleAddItem('required_methods', newMethod)}
                                    disabled={!newMethod.trim()}
                                >
                                    Add
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                    
                    {/* Required Frameworks */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                Required Frameworks
                                <Chip 
                                    label={`${editableRequirements.required_frameworks.length} found`} 
                                    size="small" 
                                    color={editableRequirements.required_frameworks.length > 0 ? "success" : "default"}
                                />
                            </Typography>
                            
                            {editableRequirements.required_frameworks.length > 0 ? (
                                <Box sx={{ mb: 2 }}>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {editableRequirements.required_frameworks.map((framework, index) => (
                                            <Chip
                                                key={index}
                                                label={framework}
                                                onDelete={() => handleRemoveItem('required_frameworks', index)}
                                                color="secondary"
                                                sx={{ mb: 1 }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            ) : (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        No frameworks detected. Add some below to improve matching.
                                    </Typography>
                                </Alert>
                            )}
                            
                            <Divider sx={{ mb: 2 }} />
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Add more frameworks:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="e.g., OECD-DAC, Theory of Change"
                                    value={newFramework}
                                    onChange={(e) => setNewFramework(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleAddItem('required_frameworks', newFramework);
                                        }
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={() => handleAddItem('required_frameworks', newFramework)}
                                    disabled={!newFramework.trim()}
                                >
                                    Add
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                    
                    {/* Specific Techniques */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                Specific Techniques
                                <Chip 
                                    label={`${editableRequirements.specific_techniques.length} found`} 
                                    size="small" 
                                    color={editableRequirements.specific_techniques.length > 0 ? "success" : "default"}
                                />
                            </Typography>
                            
                            {editableRequirements.specific_techniques.length > 0 ? (
                                <Box sx={{ mb: 2 }}>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {editableRequirements.specific_techniques.map((technique, index) => (
                                            <Chip
                                                key={index}
                                                label={technique}
                                                onDelete={() => handleRemoveItem('specific_techniques', index)}
                                                color="info"
                                                sx={{ mb: 1 }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            ) : (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        No techniques detected. Add some below to improve matching.
                                    </Typography>
                                </Alert>
                            )}
                            
                            <Divider sx={{ mb: 2 }} />
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Add more techniques:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="e.g., Focus groups, Key informant interviews"
                                    value={newTechnique}
                                    onChange={(e) => setNewTechnique(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleAddItem('specific_techniques', newTechnique);
                                        }
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={() => handleAddItem('specific_techniques', newTechnique)}
                                    disabled={!newTechnique.trim()}
                                >
                                    Add
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                    
                    {/* Additional Context */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Additional Context (Optional)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Add any additional context, keywords, or requirements to improve proposal matching.
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={8}
                                placeholder="E.g., 'Focus on post-conflict settings', 'Include cost-benefit analysis', 'Emphasize participatory approaches'..."
                                value={editableRequirements.additional_context}
                                onChange={(e) => setEditableRequirements(prev => 
                                    prev ? { ...prev, additional_context: e.target.value } : prev
                                )}
                            />
                        </Paper>
                    </Grid>
                    
                    {/* Action Buttons */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                onClick={handleReset}
                                startIcon={<CloseIcon />}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="large"
                                onClick={handleFindMatchingCVs}
                                disabled={isMatchingCVs || isProcessing}
                                startIcon={isMatchingCVs ? <CircularProgress size={20} /> : <SearchIcon />}
                            >
                                {isMatchingCVs ? 'Finding CVs...' : 'Find Matching CVs'}
                            </Button>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleContinueGeneration}
                                disabled={isProcessing}
                                startIcon={isProcessing ? <CircularProgress size={20} /> : <SearchIcon />}
                            >
                                {isProcessing ? 'Processing...' : 'Find Similar Proposals'}
                            </Button>
                        </Box>
                    </Grid>
                    
                    {/* CV Matching Results */}
                    {cvMatches && cvMatches.matches.length > 0 && (
                        <Grid item xs={12}>
                            <Divider sx={{ my: 4 }} />
                            <CVMatchingResults 
                                matches={cvMatches.matches}
                                additionalMatches={cvMatches.additional_matches}
                                searchCriteria={cvMatches.search_criteria}
                            />
                        </Grid>
                    )}
                </Grid>
            ) : methodology ? (
                /* Results View - Compact professional layout */
                <>
                    {/* Header with actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                            <Typography variant="h5" gutterBottom>
                                Methodology Suggestions Generated Successfully
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {uploadedFile?.name} • {new Date().toLocaleString()}
                                {methodology?.word_count && ` • ${methodology.word_count.toLocaleString()} words`}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={2}>
                            <Tooltip title="Open Document Chat to ask questions about this ToR">
                                <Button
                                    variant="outlined"
                                    onClick={() => window.open('https://iod-dochub.iodparc.com/document-chat#/document-chat', '_blank')}
                                    startIcon={<InfoIcon />}
                                >
                                    Chat with ToR
                                </Button>
                            </Tooltip>
                            <Button
                                variant="outlined"
                                onClick={handleReset}
                                startIcon={<RefreshIcon />}
                            >
                                New Methodology
                            </Button>
                        </Stack>
                    </Box>
                    
                    <Grid container spacing={3}>
                        {/* ToR Requirements Summary - Collapsible for quick reference */}
                        <Grid item xs={12}>
                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">Extracted Requirements Summary</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0 }}>
                                    <ExtractedRequirementsSummary extraction={getFinalExtraction() || {}} />
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    
                        {/* Similar Proposals - Show which proposals were used */}
                        {similarProposals.length > 0 && (
                            <Grid item xs={12}>
                                <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="h6">Similar Proposals Used ({similarProposals.length})</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
                                            These past proposals were analyzed to generate your tailored methodology.
                                        </Typography>
                                        
                                        <Grid container spacing={2}>
                                            {similarProposals.map((proposal, idx) => (
                                                <Grid item xs={12} md={6} key={idx}>
                                                    <Box sx={{ 
                                                        p: 2, 
                                                        borderRadius: 2,
                                                        bgcolor: 'rgba(255,255,255,0.05)',
                                                        border: '1px solid rgba(0, 165, 153, 0.2)',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(255,255,255,0.08)',
                                                            border: '1px solid rgba(0, 165, 153, 0.4)'
                                                        }
                                                    }}>
                                                        <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                                                            Proposal {idx + 1}
                                                        </Typography>
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                            <Chip 
                                                                label={`Match: ${Math.round(proposal.similarity_score * 100)}%`}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: 'rgba(0, 165, 153, 0.2)',
                                                                    color: '#00A599',
                                                                    border: '1px solid rgba(0, 165, 153, 0.5)',
                                                                    fontWeight: 500
                                                                }}
                                                            />
                                                            {(proposal.proposal_name || proposal.client || proposal.proposal_id) && (
                                                                <Chip 
                                                                    label={proposal.proposal_name || `${proposal.client || 'Proposal'} #${proposal.proposal_id || idx + 1}`}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: 'rgba(255,255,255,0.1)',
                                                                        color: 'rgba(255,255,255,0.7)',
                                                                        border: '1px solid rgba(255,255,255,0.2)',
                                                                        maxWidth: '100%',
                                                                        height: 'auto',
                                                                        '& .MuiChip-label': {
                                                                            whiteSpace: 'normal',
                                                                            wordBreak: 'break-word',
                                                                            padding: '4px 8px'
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>
                        )}
                    
                        {/* Generated Methodology - The main deliverable */}
                        <Grid item xs={12}>
                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                                        <Typography variant="h6">Generated Methodology</Typography>
                                        <Tooltip title="Copy to clipboard">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<ContentCopyIcon />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCopyMethodology();
                                                }}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Copy Text
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ 
                                        maxHeight: '600px', 
                                        overflowY: 'auto',
                                        pr: 2,
                                        '&::-webkit-scrollbar': {
                                            width: '8px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: '#f1f1f1',
                                            borderRadius: '4px',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: '#00A599',
                                            borderRadius: '4px',
                                        },
                                        '&::-webkit-scrollbar-thumb:hover': {
                                            background: '#008577',
                                        },
                                    }}>
                                        {formatMethodologyText(methodology.generated_methodology || methodology.methodology_text || '')}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                </Grid>
                </>
            ) : null}
            
            {/* Copy Success Notification */}
            <Snackbar
                open={showCopyNotification}
                autoHideDuration={3000}
                onClose={() => setShowCopyNotification(false)}
                message="Methodology copied to clipboard!"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
};

export default MethodologyBuilderPage;

